// src/components/auth/WorkspaceClosedOverlay.tsx
// Non-dismissible overlay for 409 WORKSPACE_CLOSED.
// No close button, no Escape handler, no click-outside dismiss.
// Only unlocked by switching to a DIFFERENT workspace via switch-context returning 200.

import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { selectBootClosedWorkspaceResponse, resetBoot, setBootLoading } from '../../store/slices/bootContextSlice';
import {
  selectCentralWorkspace as selectCentralWorkspaceAction,
  selectCentralAccessToken,
} from '../../store/slices/centralAuthSlice';
import { selectWorkspaceViaBearer } from '../../api/centralSuiteApi';
import { baseApi } from '../../api/baseApi';
import { useBootGate } from './BootGate';
import LoadingSpinner from '../common/LoadingSpinner';

export default function WorkspaceClosedOverlay() {
  const dispatch = useAppDispatch();
  const closedResponse = useAppSelector(selectBootClosedWorkspaceResponse);
  const accessToken = useAppSelector(selectCentralAccessToken);
  const { rerunBoot } = useBootGate();
  const [switchingId, setSwitchingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const otherWorkspaces = closedResponse?.otherWorkspaces || [];

  const handleSwitchWorkspace = async (workspaceId: string) => {
    if (!accessToken) return;
    setSwitchingId(workspaceId);
    setError(null);

    try {
      // Get new token via bearer-token select-workspace (NOT session cookies)
      const result = await selectWorkspaceViaBearer(accessToken, workspaceId);

      // Update token in Redux + localStorage
      dispatch(
        selectCentralWorkspaceAction({
          workspace: {
            id: result.workspace.id,
            name: result.workspace.name,
            slug: result.workspace.slug,
            type: result.workspace.type as 'personal' | 'business',
            role: result.workspace.role as any,
            isPrimary: result.workspace.isPrimary,
          },
          accessToken: result.accessToken,
          expiresIn: result.expiresIn,
        })
      );

      // Clear all RTK Query caches
      dispatch(baseApi.util.resetApiState());

      // Reset boot and re-run with new token
      dispatch(resetBoot());
      dispatch(setBootLoading());

      // BootGate will re-run switch-context with the new token
      rerunBoot();
    } catch (err: any) {
      setError(
        err?.message || 'Failed to switch workspace. Please try again.'
      );
      setSwitchingId(null);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-gray-900/80 backdrop-blur-sm"
      role="alertdialog"
      aria-modal="true"
      aria-label="Workspace closed"
    >
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Workspace Unavailable
            </h2>
            <p className="text-sm text-gray-500">
              This workspace has been closed or suspended.
            </p>
          </div>
        </div>

        {otherWorkspaces.length > 0 ? (
          <>
            <p className="text-sm text-gray-600 mb-4">
              Please switch to another workspace to continue:
            </p>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {otherWorkspaces.map((ws) => (
                <button
                  key={ws.id}
                  onClick={() => handleSwitchWorkspace(ws.id)}
                  disabled={switchingId !== null}
                  className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-colors disabled:opacity-50 disabled:cursor-wait"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{ws.name}</p>
                      <p className="text-xs text-gray-500 capitalize">
                        {ws.type} · {ws.role}
                      </p>
                    </div>
                    {switchingId === ws.id && (
                      <LoadingSpinner size="sm" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </>
        ) : (
          <p className="text-sm text-gray-600">
            No other workspaces are available. Please contact your administrator.
          </p>
        )}

        {error && (
          <p className="mt-4 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
            {error}
          </p>
        )}
      </div>
    </div>,
    document.body
  );
}
